import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@/lib/utils";

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
    disableAnimation?: boolean;
    disablePortal?: boolean;
    portalContainer?: HTMLElement | null;
  }
>(({ className, align = "center", sideOffset = 4, disableAnimation = false, disablePortal = false, portalContainer, avoidCollisions: avoidCollisionsProp, ...props }, ref) => {
  // On phones/tablets always keep pop-ups on screen, even if a caller disabled collision handling
  const isSmallScreen = typeof window !== "undefined" && window.innerWidth < 1024;
  const avoidCollisions = isSmallScreen ? true : (avoidCollisionsProp ?? true);
  // ALWAYS use Portal - either to custom container or document.body
  // This ensures popovers escape restrictive containers
  const content = (
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      avoidCollisions={avoidCollisions}
      collisionPadding={8}
      sticky="always"
      className={cn(
        "rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none pointer-events-auto",
        // Hard-coded min dimensions to prevent squashing
        "min-w-[min(200px,var(--radix-popover-content-available-width,100vw))] max-w-[calc(100vw-16px)] max-h-[var(--radix-popover-content-available-height,90vh)] overflow-auto",
        // High z-index for embed mode
        disablePortal ? "z-[999999]" : (portalContainer ? "z-[9999]" : "z-[250]"),
        !disableAnimation && "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    />
  );

  // Always use Portal, targeting document.body explicitly in embed mode
  // This allows content to escape iframe/div constraints
  return (
    <PopoverPrimitive.Portal container={disablePortal ? document.body : (portalContainer ?? undefined)}>
      {content}
    </PopoverPrimitive.Portal>
  );
});
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent };
